import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import IncompatiblePrescription from './IncompatiblePrescription';
import GenericButton from './GenericButton';
import { useSelector, useDispatch } from 'react-redux';
import utils from '../app/utils';
import constants from '../app/constants';
import {
    setSelectedPrescriptionObject,
    setIsPrescriptionModalOpen,
    setIsPDModalOpen,
} from '../app/mainSlice';
import { isMobile } from 'react-device-detect';

/**
 * @param props.config
 * @param props.isFormVisible
 */
const FullPrescriptionForm = props => {
    const dispatch = useDispatch();
    const labels = useSelector(state => state.main.labels);
    const [compatibleFramesUrl, setCompatibleFramesUrl] = useState(null);
    const [compatibleFramesPrescriptionObject, setCompatibleFramesPrescriptionObject] = useState(
        null
    );
    const data = useSelector(state => state.main.data);
    const extPrescriptionObject = useSelector(state => state.main.extPrescriptionObject);
    const selectedPrescriptionObject = useSelector(state => state.main.selectedPrescriptionObject);
    const isPrescriptionModalOpen = useSelector(state => state.main.isPrescriptionModalOpen);
    const isPDModalOpen = useSelector(state => state.main.isPDModalOpen);
    const [axisODValError, setAxisODValError] = useState(false); //used to show/hide axis-error
    const [axisOSValError, setAxisOSValError] = useState(false); //used to show/hide axis-error

    const isAnyModalOpen = isPrescriptionModalOpen || isPDModalOpen;

    const openPrescriptionModal = event => {
        if ((event.type === 'keydown' && event.key === 'Enter') || event.type === 'click') {
            dispatch(setIsPrescriptionModalOpen(true));
        }
    };
    const openPupillaryDistanceModal = event => {
        if ((event.type === 'keydown' && event.key === 'Enter') || event.type === 'click') {
            dispatch(setIsPDModalOpen(true));
        }
    };
    const {
        register,
        errors,
        handleSubmit,
        watch,
        clearErrors,
        setError,
        reset,
        setValue,
    } = useForm();

    const sphOdVal = watch('sph_od');
    const sphOsVal = watch('sph_os');
    const cylOdVal = watch('cyl_od');
    const cylOsVal = watch('cyl_os');
    const addOdVal = watch('add_od');
    const addOsVal = watch('add_os');
    const axisOdVal = watch('axis_od');
    const axisOsVal = watch('axis_os');
    const pdCheckVal = watch('pdCheck');

    const validationCondition =
        !errors.cyl_os &&
        !errors.cyl_od &&
        !errors.sph_os &&
        !errors.sph_od &&
        !errors.add_os &&
        !errors.add_od &&
        sphOdVal != '' &&
        sphOsVal != '' &&
        cylOdVal != '' &&
        cylOsVal != '' &&
        sphOdVal != undefined &&
        sphOsVal != undefined &&
        cylOdVal != undefined &&
        cylOsVal != undefined &&
        (!props.isFullPrescription ||
            (addOdVal != '' && addOsVal != '' && !axisODValError && !axisOSValError));

    const showCheckbox =
        validationCondition &&
        addOdVal != '' &&
        addOsVal != '' &&
        !axisODValError &&
        !axisOSValError;

    useEffect(() => {
        if (props.isFormVisible) {
            if (selectedPrescriptionObject) {
                setValue('sph_od', selectedPrescriptionObject.SPH.OD);
                setValue('sph_os', selectedPrescriptionObject.SPH.OS);
                setValue('cyl_od', selectedPrescriptionObject.CYL.OD);
                setValue('cyl_os', selectedPrescriptionObject.CYL.OS);
                if (selectedPrescriptionObject.PD) {
                    if (selectedPrescriptionObject.PD.OD) {
                        setValue('pd_od', selectedPrescriptionObject.PD.OD);
                    }
                    if (selectedPrescriptionObject.PD.OS) {
                        setValue('pd_os', selectedPrescriptionObject.PD.OS);
                    }
                }
                if (props.isFullPrescription) {
                    if (selectedPrescriptionObject.ADD) {
                        if (selectedPrescriptionObject.ADD.OS) {
                            setValue('add_os', selectedPrescriptionObject.ADD.OS);
                        }
                        if (selectedPrescriptionObject.ADD.OD) {
                            setValue('add_od', selectedPrescriptionObject.ADD.OD);
                        }
                    }
                    if (selectedPrescriptionObject.AX) {
                        if (selectedPrescriptionObject.AX.OS) {
                            setValue('axis_os', selectedPrescriptionObject.AX.OS);
                        }
                        if (selectedPrescriptionObject.AX.OD) {
                            setValue('axis_od', selectedPrescriptionObject.AX.OD);
                        }
                    }
                }
            } else if (extPrescriptionObject) {
                setValue('sph_od', extPrescriptionObject.SPH.OD);
                setValue('sph_os', extPrescriptionObject.SPH.OS);
                setValue('cyl_od', extPrescriptionObject.CYL.OD);
                setValue('cyl_os', extPrescriptionObject.CYL.OS);
                if (extPrescriptionObject.PD) {
                    if (extPrescriptionObject.PD.OD) {
                        setValue('pd_od', extPrescriptionObject.PD.OD);
                    }
                    if (extPrescriptionObject.PD.OS) {
                        setValue('pd_os', extPrescriptionObject.PD.OS);
                    }
                }
                if (props.isFullPrescription) {
                    if (extPrescriptionObject.ADD) {
                        if (extPrescriptionObject.ADD.OS) {
                            setValue('add_os', extPrescriptionObject.ADD.OS);
                        }
                        if (extPrescriptionObject.ADD.OD) {
                            setValue('add_od', extPrescriptionObject.ADD.OD);
                        }
                    }
                    if (extPrescriptionObject.AX) {
                        if (extPrescriptionObject.AX.OS) {
                            setValue('axis_os', extPrescriptionObject.AX.OS);
                        }
                        if (extPrescriptionObject.AX.OD) {
                            setValue('axis_od', extPrescriptionObject.AX.OD);
                        }
                    }
                }
            }
        }
    }, [extPrescriptionObject, selectedPrescriptionObject]);

    const onSubmit = formData => {
        console.log(formData);

        const prescriptionObject = utils.getFullPrescriptionObject(formData);

        //check if packages include prescription compatible lenses
        if (
            utils.getCompatibleLensesType(data.packages, prescriptionObject) !== null &&
            utils.isFramePrescriptionCompatible(
                props.config.data.frame.rxValues,
                prescriptionObject
            )
        ) {
            if (process.env.NODE_ENV === 'development') {
                alert('There are compatible lenses!');
            }

            if (props.config.prescriptionModule.savePrescription(prescriptionObject)) {
                dispatch(setSelectedPrescriptionObject(prescriptionObject));

                props.handleConfirmPrescription({
                    prescription: 'Confirmed',
                });
            } else {
                console.error('Error while saving prescription');
            }
        } else {
            //no prescription compatible lenses
            let compatibleFramesResult = props.config.prescriptionModule.checkAvailableFrames(
                props.config.data.frame,
                prescriptionObject
            );

            if (compatibleFramesResult instanceof Promise) {
                compatibleFramesResult.then(
                    compatibleFramesUrl => {
                        if (compatibleFramesUrl) {
                            setCompatibleFramesPrescriptionObject(prescriptionObject);
                            setCompatibleFramesUrl(compatibleFramesUrl);
                        }
                    },
                    error => {
                        console.log('Unable to get compatible frames URL');
                    }
                );
            } else {
                setCompatibleFramesPrescriptionObject(prescriptionObject);
                setCompatibleFramesUrl(compatibleFramesResult);
            }

            ['sph_od', 'sph_os'].forEach(elem =>
                setError(elem, {
                    type: 'manual',
                })
            );

            setError('rxIncompatible', {
                type: 'manual',
            });
        }
    };

    const handleAxisCylChange = () => {
        //od
        if (
            axisOdVal.length > 0 &&
            parseFloat(axisOdVal) == 0 &&
            cylOdVal.length > 0 &&
            parseFloat(cylOdVal) !== 0
        ) {
            setAxisODValError(true);
        } else {
            setAxisODValError(false);
        }

        //os
        if (
            axisOsVal.length > 0 &&
            parseFloat(axisOsVal) == 0 &&
            cylOsVal.length > 0 &&
            parseFloat(cylOsVal) !== 0
        ) {
            setAxisOSValError(true);
        } else {
            setAxisOSValError(false);
        }
    };

    return (
        <div className="prescription-container prescription-step-container">
            <div className="input-message">
                {extPrescriptionObject
                    ? labels.prescription.missingInputMessage
                    : labels.prescription.defaultInputMessage}
            </div>

            <a
                htmlFor="prescription_modal"
                className="prescription-info"
                onClick={openPrescriptionModal}
                tabIndex={isAnyModalOpen ? '-1' : '0'}
                onKeyDown={openPrescriptionModal}
            >
                {labels.prescription.readPrescription}
            </a>
            <form id="prescription-form" onSubmit={handleSubmit(onSubmit)}>
                <div className=" prescription-form-container">
                    <div className="prescription-form-inner-content">
                        <div className="prescription-input-content">
                            <div className="right-content">
                                <div className="title">
                                    {' '}
                                    <span className="bold">OD</span>
                                    <span>(Right eye)</span>
                                </div>
                                <div className="input-content">
                                    <div className="sph-content">
                                        <label htmlFor="sph_od" className="rx-label bold">
                                            SPH
                                        </label>
                                        <select
                                            name="sph_od"
                                            id="sph_od"
                                            ref={register({ required: true })}
                                            className={
                                                (errors.sph_od ? ' error' : '') +
                                                (parseFloat(sphOdVal) * parseFloat(sphOsVal) < 0
                                                    ? ' warning'
                                                    : '')
                                            }
                                            tabIndex={isAnyModalOpen ? '-1' : '0'}
                                        >
                                            {utils
                                                .getValuesArray(
                                                    constants.prescriptionDefaults.sphMin,
                                                    constants.prescriptionDefaults.sphMax,
                                                    constants.prescriptionDefaults.sphRes,
                                                    '--'
                                                )
                                                .map((e, key) => {
                                                    return (
                                                        <option key={key} value={e.value}>
                                                            {e.name}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                    <div className="cyl-content">
                                        <label htmlFor="cyl_od" className="rx-label bold">
                                            CYL
                                        </label>
                                        <select
                                            name="cyl_od"
                                            id="cyl_od"
                                            onChange={handleAxisCylChange}
                                            ref={register({ required: true })}
                                            className={errors.cyl_od ? ' error' : ''}
                                            tabIndex={isAnyModalOpen ? '-1' : '0'}
                                        >
                                            {utils
                                                .getValuesArray(
                                                    constants.prescriptionDefaults.cylMin,
                                                    constants.prescriptionDefaults.cylMax,
                                                    constants.prescriptionDefaults.cylRes,
                                                    '--'
                                                )
                                                .map((e, key) => {
                                                    return (
                                                        <option key={key} value={e.value}>
                                                            {e.name}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                    <div className="axis-content">
                                        <label
                                            htmlFor="axis_od"
                                            className={
                                                'rx-label' +
                                                (props.isFullPrescription ? '' : ' disabled')
                                            }
                                        >
                                            AXIS
                                        </label>
                                        <input
                                            name="axis_od"
                                            id="axis_od"
                                            type="number"
                                            onChange={handleAxisCylChange}
                                            ref={register({ min: 0, max: 180 })}
                                            disabled={props.isFullPrescription ? '' : 'disabled'}
                                            className={
                                                (errors.axis_od || axisODValError ? ' error' : '') +
                                                (props.isFullPrescription ? '' : ' disabled')
                                            }
                                        />
                                    </div>
                                    <div className="add-content">
                                        <label
                                            htmlFor="add_od"
                                            className={
                                                'rx-label' +
                                                (props.isFullPrescription ? '' : ' disabled')
                                            }
                                        >
                                            ADD
                                        </label>
                                        <select
                                            name="add_od"
                                            id="add_od"
                                            disabled={props.isFullPrescription ? '' : 'disabled'}
                                            className={
                                                (errors.axis_od || axisODValError ? ' error' : '') +
                                                (props.isFullPrescription ? '' : ' disabled')
                                            }
                                            ref={
                                                props.isFullPrescription
                                                    ? register({ required: true })
                                                    : register
                                            }
                                        >
                                            {utils
                                                .getValuesArray(
                                                    constants.prescriptionDefaults.addMin,
                                                    constants.prescriptionDefaults.addMax,
                                                    constants.prescriptionDefaults.addRes,
                                                    '--'
                                                )
                                                .map((e, key) => {
                                                    return (
                                                        <option key={key} value={e.value}>
                                                            {e.name}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <span className="prescription-error">
                                {!errors.axis_od &&
                                    !errors.axis_os &&
                                    !errors.rxIncompatible &&
                                    axisODValError &&
                                    isMobile && (
                                        <div className="prescription-warning axis-error od-error">
                                            <span className="error-title">
                                                {labels.prescription.axisError}
                                            </span>
                                            <span>{labels.prescription.axisSubErrorZero}</span>
                                        </div>
                                    )}
                            </span>

                            <div className="left-content">
                                <div className="title">
                                    <span className="bold">OS</span>
                                    <span>(Left eye)</span>
                                </div>
                                <div className="input-content">
                                    <div className="sph-content">
                                        <label htmlFor="sph_os" className="rx-label bold">
                                            SPH
                                        </label>
                                        <select
                                            name="sph_os"
                                            id="sph_os"
                                            ref={register({ required: true })}
                                            className={
                                                (errors.sph_os ? ' error' : '') +
                                                (parseFloat(sphOdVal) * parseFloat(sphOsVal) < 0
                                                    ? ' warning'
                                                    : '')
                                            }
                                            tabIndex={isAnyModalOpen ? '-1' : '0'}
                                        >
                                            {utils
                                                .getValuesArray(
                                                    constants.prescriptionDefaults.sphMin,
                                                    constants.prescriptionDefaults.sphMax,
                                                    constants.prescriptionDefaults.sphRes,
                                                    '--'
                                                )
                                                .map((e, key) => {
                                                    return (
                                                        <option key={key} value={e.value}>
                                                            {e.name}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                    <div className="cyl-content">
                                        <label htmlFor="cyl_os" className="rx-label bold">
                                            CYL
                                        </label>
                                        <select
                                            name="cyl_os"
                                            id="cyl_os"
                                            onChange={handleAxisCylChange}
                                            ref={register({ required: true })}
                                            className={errors.cyl_os ? ' error' : ''}
                                            tabIndex={isAnyModalOpen ? '-1' : '0'}
                                        >
                                            {utils
                                                .getValuesArray(
                                                    constants.prescriptionDefaults.cylMin,
                                                    constants.prescriptionDefaults.cylMax,
                                                    constants.prescriptionDefaults.cylRes,
                                                    '--'
                                                )
                                                .map((e, key) => {
                                                    return (
                                                        <option key={key} value={e.value}>
                                                            {e.name}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                    <div className="axis-content">
                                        <label
                                            htmlFor="axis_os"
                                            className={
                                                'rx-label' +
                                                (props.isFullPrescription ? '' : ' disabled')
                                            }
                                        >
                                            AXIS
                                        </label>
                                        <input
                                            name="axis_os"
                                            id="axis_os"
                                            type="number"
                                            onChange={handleAxisCylChange}
                                            ref={register({ min: 0, max: 180 })}
                                            disabled={props.isFullPrescription ? '' : 'disabled'}
                                            className={
                                                (errors.axis_os || axisOSValError ? ' error' : '') +
                                                (props.isFullPrescription ? '' : ' disabled')
                                            }
                                        />
                                    </div>
                                    <div className="add-content">
                                        <label
                                            htmlFor="add_os"
                                            className={
                                                'rx-label' +
                                                (props.isFullPrescription ? '' : ' disabled')
                                            }
                                        >
                                            ADD
                                        </label>
                                        <select
                                            name="add_os"
                                            id="add_os"
                                            disabled={props.isFullPrescription ? '' : 'disabled'}
                                            className={
                                                (errors.axis_od || axisODValError ? ' error' : '') +
                                                (props.isFullPrescription ? '' : ' disabled')
                                            }
                                            ref={
                                                props.isFullPrescription
                                                    ? register({ required: true })
                                                    : register
                                            }
                                        >
                                            {utils
                                                .getValuesArray(
                                                    constants.prescriptionDefaults.addMin,
                                                    constants.prescriptionDefaults.addMax,
                                                    constants.prescriptionDefaults.addRes,
                                                    '--'
                                                )
                                                .map((e, key) => {
                                                    return (
                                                        <option key={key} value={e.value}>
                                                            {e.name}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <input type="hidden" name="rxIncompatible" ref={register} value="" />

                        {errors.rxIncompatible ? (
                            <IncompatiblePrescription
                                config={props.config}
                                prescriptionObject={compatibleFramesPrescriptionObject}
                                framesUrl={compatibleFramesUrl}
                            />
                        ) : null}

                        {parseFloat(sphOdVal) * parseFloat(sphOsVal) < 0 &&
                            !errors.sph_od &&
                            !errors.sph_os &&
                            !errors.cyl_od &&
                            !errors.cyl_os && (
                                <div className="prescription-warning">
                                    {labels.prescription.positiveNegativeError}
                                </div>
                            )}
                        <span className="prescription-error">
                            {(errors.sph_od || errors.sph_os || errors.cyl_od || errors.cyl_os) &&
                                !errors.rxIncompatible &&
                                !errors.axis_od &&
                                !errors.axis_os &&
                                labels.prescription.missingValueError}

                            {(errors.axis_od || errors.axis_os) && !errors.rxIncompatible && (
                                <div className="prescription-warning axis-error">
                                    <span className="error-title">
                                        {labels.prescription.axisError}
                                    </span>
                                    <span>{labels.prescription.axisSubError}</span>
                                </div>
                            )}
                            {!errors.axis_od &&
                                !errors.axis_os &&
                                !errors.rxIncompatible &&
                                (axisODValError || axisOSValError) &&
                                !isMobile && (
                                    <div className="prescription-warning axis-error od-error">
                                        <span className="error-title">
                                            {labels.prescription.axisError}
                                        </span>
                                        <span>{labels.prescription.axisSubErrorZero}</span>
                                    </div>
                                )}
                            {!errors.axis_od &&
                                !errors.axis_os &&
                                !errors.rxIncompatible &&
                                axisOSValError &&
                                isMobile && (
                                    <div className="prescription-warning axis-error os-error">
                                        <span className="error-title">
                                            {labels.prescription.axisError}
                                        </span>
                                        <span>{labels.prescription.axisSubErrorZero}</span>
                                    </div>
                                )}
                        </span>

                        <div className="pd-container">
                            <div className="pd-top">
                                <div className="title">
                                    <span className="bold">PD</span>
                                    <span>(Pupillary Distance)</span>
                                </div>

                                <a
                                    htmlFor="pd_modal"
                                    className="find-pd"
                                    onClick={openPupillaryDistanceModal}
                                    tabIndex={isAnyModalOpen ? '-1' : '0'}
                                    onKeyDown={openPupillaryDistanceModal}
                                >
                                    {labels.prescription.pdHelp}
                                </a>
                            </div>
                            <div className="pd-values">
                                <div className="right">
                                    <label
                                        htmlFor="pd_od"
                                        className={pdCheckVal ? '' : 'right-label-hidden'}
                                    >
                                        Right
                                    </label>
                                    <select
                                        name="pd_od"
                                        id="pd_od"
                                        ref={register}
                                        tabIndex={isAnyModalOpen ? '-1' : '0'}
                                    >
                                        {pdCheckVal
                                            ? utils
                                                  .getValuesArray(
                                                      constants.prescriptionDefaults.pdHalfMin,
                                                      constants.prescriptionDefaults.pdHalfMax,
                                                      constants.prescriptionDefaults.pdHalfRes,
                                                      '--'
                                                  )
                                                  .map((e, key) => {
                                                      return (
                                                          <option key={key} value={e.value}>
                                                              {e.name}
                                                          </option>
                                                      );
                                                  })
                                            : utils
                                                  .getValuesArray(
                                                      constants.prescriptionDefaults.pdMin,
                                                      constants.prescriptionDefaults.pdMax,
                                                      constants.prescriptionDefaults.pdRes,
                                                      '--'
                                                  )
                                                  .map((e, key) => {
                                                      return (
                                                          <option key={key} value={e.value}>
                                                              {e.name}
                                                          </option>
                                                      );
                                                  })}
                                    </select>
                                </div>

                                {pdCheckVal && (
                                    <div className="left">
                                        <label className="right-label">Left</label>
                                        <select name="pd_os" id="pd_os" ref={register}>
                                            {utils
                                                .getValuesArray(
                                                    constants.prescriptionDefaults.pdHalfMin,
                                                    constants.prescriptionDefaults.pdHalfMax,
                                                    constants.prescriptionDefaults.pdHalfRes,
                                                    '--'
                                                )
                                                .map((e, key) => {
                                                    return (
                                                        <option key={key} value={e.value}>
                                                            {e.name}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                )}
                            </div>
                            <div className="two-pd-slider">
                                <label className="pd-switch">
                                    <span style={{ display: 'none' }}>Slider</span>
                                    <input
                                        type="checkbox"
                                        name="pdCheck"
                                        id="pdCheck"
                                        ref={register}
                                        tabIndex={isAnyModalOpen ? '-1' : '0'}
                                    />
                                    <span className="slider round" />
                                </label>
                                <span className="two-pd-label">
                                    {labels.prescription.twoPDNumbers}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={showCheckbox ? '' : 'external-message-container'}>
                    {showCheckbox && (
                        <label className="lc-control lc-control--checkbox proceed-checkbox">
                            {labels.prescription.proceedCheckbox}
                            <input
                                type="checkbox"
                                name="proceedCheck"
                                id="proceedCheck"
                                ref={register({ required: true })}
                                tabIndex={isAnyModalOpen ? '-1' : '0'}
                            />
                            <div className="lc-control__indicator indicator"></div>
                        </label>
                    )}
                    <div className="proceed-checkbox-container">
                        {errors.proceedCheck && (
                            <div className="proceed-error">
                                {labels.prescription.proceedCheckboxError}
                            </div>
                        )}
                    </div>

                    {!showCheckbox && (
                        <div className="external-message">
                            {validationCondition
                                ? labels.prescription.readyMessage
                                : labels.prescription.skipMessage}
                        </div>
                    )}
                    {validationCondition && !errors.rxIncompatible ? (
                        <GenericButton
                            className="border-button apply-button"
                            type="submit"
                            title={labels.prescription.submitButton}
                            tabIndex="0"
                        >
                            {labels.prescription.submitButton}
                        </GenericButton>
                    ) : (
                        <>
                            {errors.rxIncompatible ? (
                                <GenericButton
                                    className="border-button clear-button"
                                    title={labels.prescription.clearButton}
                                    tabIndex="0"
                                    handleClick={() => {
                                        props.config.prescriptionModule.clearPrescription();
                                        dispatch(setSelectedPrescriptionObject(null));
                                        clearErrors();
                                        reset();
                                        clearErrors();
                                    }}
                                >
                                    {labels.prescription.clearButton}
                                </GenericButton>
                            ) : (
                                <GenericButton
                                    className="border-button skip-button"
                                    title={labels.prescription.skipButton}
                                    tabIndex={isAnyModalOpen ? '-1' : '0'}
                                    handleClick={() => {
                                        clearErrors();
                                        reset();
                                        props.handleConfirmPrescription({
                                            prescription: 'Skipped',
                                        });
                                        dispatch(setSelectedPrescriptionObject(null));
                                    }}
                                >
                                    {labels.prescription.skipButton}
                                </GenericButton>
                            )}
                        </>
                    )}
                </div>
            </form>
        </div>
    );
};
export default FullPrescriptionForm;
